import { useQueryClient } from '@tanstack/react-query';
import { UserApiModel } from '../../../../types/models/user.model';
import { AddDomainToUserApiRequest } from '../../../../types/requests/users/add-domain-to-user.request';
import { AddRoleToUserApiRequest } from '../../../../types/requests/users/add-role-to-user.request';
import { CreateUserApiRequest } from '../../../../types/requests/users/create-user.request';
import { DeleteUserApiRequest } from '../../../../types/requests/users/delete-user.request';
import { GetUserApiRequest } from '../../../../types/requests/users/get-user.request';
import { RemoveDomainFromUserApiRequest } from '../../../../types/requests/users/remove-domain-from-user.request';
import { RemoveRoleFromUserApiRequest } from '../../../../types/requests/users/remove-role-from-user.request';
import { SearchUsersApiRequest } from '../../../../types/requests/users/search-users.request';
import { UpdateUserApiRequest } from '../../../../types/requests/users/update-user.request';
import {
  addDomainToUser,
  addRoleToUser,
  createUser,
  deleteUser,
  getAuthenticatedUser,
  getUser,
  removeRoleFromUser,
  removeDomainFromUser,
  searchUsers,
  updateUser,
  updateAuthenticatedUser,
} from '../../api/users.api';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';
import { UpdateAuthenticatedUserApiRequest } from '../../../../types/requests/users/update-authenticated-user.request';

const baseQueryKey = 'user';
const searchQueryKey = 'search';

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateUserApiRequest) => (await createUser(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, user: data }] as const;
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateUserApiRequest) => (await updateUser(params)).data,
    onSuccess: (user) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === searchQueryKey || (queryKey[1] as GetUserApiRequest)?.userId === user.id),
      }),
  });
  return [request, { ...result, user: data }] as const;
};

export const useGetAuthenticatedUser = () => {
  const { data, ...result } = useAxiosQuery({
    queryKey: [baseQueryKey, 'me'],
    queryFn: async () => (await getAuthenticatedUser()).data,
  });
  return { ...result, user: data } as const;
};

export const useUpdateAuthenticatedUser = () => {
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateAuthenticatedUserApiRequest) => (await updateAuthenticatedUser(params)).data,
  });
  return [request, { ...result, user: data }] as const;
}

export const useGetUser = <T extends UserApiModel>(params: GetUserApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getUser<T>(params)).data,
    enabled: Boolean(params.userId && !Number.isNaN(params.userId)),
  });
  return { ...result, user: data } as const;
};

export const useSearchUsers = (params: SearchUsersApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchUsers({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteUserApiRequest) => (await deleteUser(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useAddRoleToUser = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: AddRoleToUserApiRequest) => (await addRoleToUser(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey && (queryKey[1] as GetUserApiRequest)?.userId === params.userId,
      }),
  });
  return [request, result] as const;
};

export const useRemoveRoleFromUser = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: RemoveRoleFromUserApiRequest) => (await removeRoleFromUser(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey && (queryKey[1] as GetUserApiRequest)?.userId === params.userId,
      }),
  });
  return [request, result] as const;
};

export const useAddDomainToUser = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: AddDomainToUserApiRequest) => (await addDomainToUser(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey && (queryKey[1] as GetUserApiRequest)?.userId === params.userId,
      }),
  });
  return [request, result] as const;
};

export const useRemoveDomainFromUser = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: RemoveDomainFromUserApiRequest) => (await removeDomainFromUser(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey && (queryKey[1] as GetUserApiRequest)?.userId === params.userId,
      }),
  });
  return [request, result] as const;
};
